import React from 'react';
import {
  Table,
  useTable,
  useTheme,
  IconButton,
  DeleteIcon,
  MRT_Row,
  Button,
  AddCircleIcon,
} from '@cherre-frontend/ui';
import { useMappingColumnDefinitions } from 'src/products/models-library/hooks/useColumnDefinitions';
import type { UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import { MappingFieldsTableValues, TableMode } from '../schema';

export type SchemaTableProps = {
  tableMode: TableMode;
  fieldNameOptions: Array<{ label: string; value: string }>;
  setMappingValues: UseFormSetValue<MappingFieldsTableValues>;
  getMappingValues: UseFormGetValues<MappingFieldsTableValues>;
};

export const emptyMappingRow: Required<
  MappingFieldsTableValues['fields'][number]
> = {
  fieldName: '',
  displayName: '',
  descriptionName: '',
  mappingName: '',
  mappingValue: '',
  required: true,
  targetName: '',
  displayPreference: 'do_not_display',
};

export const SchemaTable: React.FC<SchemaTableProps> = ({
  tableMode,
  fieldNameOptions,
  getMappingValues,
  setMappingValues,
}) => {
  const theme = useTheme();
  const mappingValues = getMappingValues('fields');

  const handleAddRowClick = () => {
    setMappingValues?.('fields', [...mappingValues, emptyMappingRow]);
  };

  const handleDeleteRowClick = (
    row: MRT_Row<MappingFieldsTableValues['fields'][number]>
  ) => {
    setMappingValues?.(
      'fields',
      mappingValues.filter((_, index) => index !== row.index)
    );
  };

  const columns = useMappingColumnDefinitions({
    getMappingValues,
    setMappingValues,
    fieldNameOptions,
    tableMode,
  });

  const table = useTable({
    data: mappingValues,
    layoutMode: 'grid',
    skeletonRowCount: 5,
    columns,
    enableSorting: false,
    enableColumnResizing: true,
    enableRowSelection: false,
    enableRowActions: tableMode === TableMode.Create,
    positionActionsColumn: 'last',
    displayColumnDefOptions: { 'mrt-row-actions': { size: 75 } },
    renderRowActions: ({ row }) => (
      <IconButton onClick={() => handleDeleteRowClick(row)} color='error'>
        <DeleteIcon />
      </IconButton>
    ),
    enableBottomToolbar: true,
    enablePagination: tableMode !== TableMode.Create,
    enableStickyHeader: true,
    muiTableProps: { sx: { height: '100%' } },
    muiTablePaperProps: {
      sx: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: 1,
        borderRadius: '16px 16px 16px 16px',
        maxHeight: '800px',
      },
      elevation: 0,
    },
    muiTableContainerProps: {
      sx: {
        flex: 1,
        border: `1px ${theme.palette.grey[300]} solid`,
        borderRadius: '16px',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: theme.palette.secondary.main,
        color: 'white',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: 'white',
        border: '0px',
      },
    },
    muiTableBodyRowProps: {
      sx: {
        '&:hover td': { backgroundColor: theme.palette.grey[200] },
      },
    },
    manualPagination: false,
    manualSorting: false,
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      columnPinning: { right: ['mrt-row-actions'] },
    },
    getRowId: (row, index) => `mapping-tab-${row.fieldName}${index.toString()}`,
    renderBottomToolbarCustomActions: () =>
      tableMode === TableMode.Create && (
        <Button startIcon={<AddCircleIcon />} onClick={handleAddRowClick}>
          Add mapped field
        </Button>
      ),
  });

  return <Table table={table} />;
};

SchemaTable.displayName = 'SchemaTable';
export default SchemaTable;
